export type ToolDisplayMeta = {
  title: string;
  subject: string;
  effect: "read" | "write";
};

const READ_TOOL_PREFIXES = [
  "get_",
  "list_",
  "read_",
  "search_",
  "find_",
  "fetch_",
] as const;

const TOOL_ACTION_PREFIXES = [
  ...READ_TOOL_PREFIXES,
  "create_",
  "update_",
  "delete_",
  "remove_",
  "remember_",
  "run_",
  "execute_",
] as const;

const TITLE_TERMS: Readonly<Record<string, string>> = {
  ai: "AI",
  bigquery: "BigQuery",
  csv: "CSV",
  ga4: "GA4",
  id: "ID",
  mcp: "MCP",
  sa: "Search Analytics",
  ua: "User Analytics",
  url: "URL",
};

const TITLE_PHRASES: Readonly<Record<string, string>> = {
  index_worker: "Index Worker",
  search_analytics: "Search Analytics",
  service_account: "Service Account",
  user_analytics: "User Analytics",
};

function getCanonicalToolName(toolName: string): string {
  return (
    toolName
      .replace(/^tool-/u, "")
      .replace(/^dynamic-tool$/u, "tool")
      .split("__")
      .at(-1)
      ?.replace(/([a-z0-9])([A-Z])/gu, "$1_$2")
      .replace(/[-\s]+/gu, "_")
      .replace(/^_+|_+$/gu, "") ?? ""
  );
}

/** tool 名を、dynamic tool を含め一貫して読める表示名へ変換する。 */
export function humanizeToolName(toolName: string): string {
  const name = getCanonicalToolName(toolName);

  if (!name) return "Tool";

  const terms = name.split("_").filter(Boolean);
  const titleTerms: string[] = [];
  for (let index = 0; index < terms.length; index += 1) {
    const term = terms[index];
    if (!term) continue;

    const phraseKey = `${term.toLowerCase()}_${terms[index + 1]?.toLowerCase() ?? ""}`;
    const knownPhrase = TITLE_PHRASES[phraseKey];
    if (knownPhrase) {
      titleTerms.push(knownPhrase);
      index += 1;
      continue;
    }

    const lower = term.toLowerCase();
    const known = TITLE_TERMS[lower];
    if (known) {
      titleTerms.push(known);
      continue;
    }
    titleTerms.push(
      index === 0 ? `${lower.charAt(0).toUpperCase()}${lower.slice(1)}` : lower,
    );
  }

  return titleTerms.join(" ");
}

function inferToolEffect(toolName: string): ToolDisplayMeta["effect"] {
  const canonicalName = getCanonicalToolName(toolName).toLowerCase();
  return READ_TOOL_PREFIXES.some((prefix) => canonicalName.startsWith(prefix))
    ? "read"
    : "write";
}

/** 個別登録に依存せず、tool 名から一貫した表示メタデータを導出する。 */
export function getToolDisplayMeta(toolName: string): ToolDisplayMeta {
  const canonicalName = getCanonicalToolName(toolName);
  const actionPrefix = TOOL_ACTION_PREFIXES.find((prefix) =>
    canonicalName.toLowerCase().startsWith(prefix),
  );
  const subjectName = actionPrefix
    ? canonicalName.slice(actionPrefix.length)
    : canonicalName;

  return {
    title: humanizeToolName(toolName),
    subject: humanizeToolName(subjectName || canonicalName),
    effect: inferToolEffect(toolName),
  };
}
